/**
 * Enhanced DataTable Component
 *
 * All-in-one entity management component with:
 * - Optional page header (title, icon, description, badge)
 * - Dynamic filters (configurable via props)
 * - Search, Sort, Export functionality
 * - Action buttons (Add, Refresh, Filter, Export)
 * - Responsive card/table views
 *
 * @example
 * ```tsx
 * <DataTable
 *   // Page Header
 *   title="Jobs"
 *   description="Manage job postings"
 *   icon={Briefcase}
 *   iconColor="#3b82f6"
 *   countBadge={{ count: jobs.length, label: 'total' }}
 *
 *   // Data
 *   data={jobs}
 *   columns={columns}
 *   getRowId={(job) => job.id}
 *
 *   // Dynamic Filters
 *   filterConfig={filterConfig}
 *   filterValues={filterValues}
 *   onFilterChange={setFilterValues}
 *
 *   // Buttons
 *   onAdd={handleAdd}
 *   onRefresh={refetch}
 *   showExportButton={true}
 *
 *   // Actions
 *   renderRowActions={renderActions}
 * />
 * ```
 */

import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronUp,
  ChevronDown,
  Plus,
  Filter,
  AlertCircle,
  RefreshCw,
  X,
  Loader2,
  LucideIcon,
} from "lucide-react";
import { TextField, InputAdornment, Button, IconButton, Checkbox } from "@mui/material";
import { useResponsive } from "../../hooks";
import { FilterBar, FilterConfig, FilterValues } from "./FilterBar";
import { ExportButton, ExportColumn } from "./ExportButton";
import { PageHeader } from "./PageHeader";

// =============================================================================
// TYPES
// =============================================================================

export interface Column<T> {
  /** Column key (can be nested using dot notation) */
  key: keyof T | string;
  /** Column header label */
  label: string;
  /** Enable sorting for this column */
  sortable?: boolean;
  /**
   * Custom value extractor used when sorting this column. Useful when the row
   * field is not directly comparable (e.g. a nested object or an enum that
   * should sort by its display label). Falls back to `row[key]` when omitted.
   */
  sortValue?: (row: T) => string | number | null | undefined;
  /** Custom render function */
  render?: (row: T) => ReactNode;
  /** Column width */
  width?: string;
  /** Minimum column width. Keeps a fixed layout from collapsing this column. */
  minWidth?: string;
  /** Hide this column on mobile card view */
  hideOnMobile?: boolean;
  /** Priority for mobile display (lower = more important, shown first) */
  mobilePriority?: number;
}

export interface DataTableProps<T> {
  // === PAGE HEADER (optional) ===
  /** Page title */
  title?: string;
  /** Red eyebrow label above the title */
  eyebrow?: string;
  /** Page description */
  description?: string;
  /** Page icon */
  icon?: LucideIcon;
  /** Icon background color */
  iconColor?: string;
  /** Count badge to display */
  countBadge?: { count: number; label?: string };
  /** Optional content rendered centered between the title and the count badge */
  headerCenter?: ReactNode;
  /** Content rendered under the title row and above the table. */
  belowHeader?: ReactNode;

  // === DATA ===
  /** Data array to display */
  data: T[];
  /** Column definitions */
  columns: Column<T>[];
  /** Row ID getter for React key */
  getRowId: (row: T) => string | number;
  /**
   * Minimum table width. Turns on a fixed column layout and scrolls horizontally
   * instead of compressing columns into the viewport.
   */
  tableMinWidth?: number;
  /** Loading state */
  isLoading?: boolean;
  /** Error message */
  error?: string | null;
  /** Replaces the table body. Header, search, and filters stay in place. */
  body?: ReactNode;

  // === FILTERS (dynamic) ===
  /** Filter configuration array */
  filterConfig?: FilterConfig[];
  /** Current filter values */
  filterValues?: FilterValues;
  /** Filter change handler */
  onFilterChange?: (values: FilterValues) => void;
  /** Show/hide filter toggle button */
  showFilterButton?: boolean;
  /** Place search, filters, and refresh on the title row, at the right. */
  toolbarInHeader?: boolean;
  /** Open the filters panel by default */
  defaultFiltersOpen?: boolean;
  /** Controlled filters panel open state */
  filtersOpen?: boolean;
  /** Controlled filters panel open change handler */
  onFiltersOpenChange?: (open: boolean) => void;
  /** Extra content rendered inline within the filters panel (e.g. a date range filter) */
  filterPanelExtra?: ReactNode;
  /** Clears every filter, including controls outside the multi-select chips */
  onClearFilters?: () => void;

  // === BUTTONS ===
  /** Add button click handler */
  onAdd?: () => void;
  /** Add button text */
  addButtonText?: string;
  /** Show/hide add button (default: true if onAdd provided) */
  showAddButton?: boolean;
  /** Disable add button */
  addButtonDisabled?: boolean;

  /** Refresh button click handler */
  onRefresh?: () => void;
  /** Show/hide refresh button (default: true if onRefresh provided) */
  showRefreshButton?: boolean;

  /** Show/hide export button */
  showExportButton?: boolean;
  /** Disable export button */
  exportButtonDisabled?: boolean;
  /** Export formats to support */
  exportFormats?: ("csv" | "excel" | "json")[];
  /** Export filename (without extension) */
  exportFilename?: string;
  /** Custom export columns */
  exportColumns?: ExportColumn<T>[];
  /** Optional async resolver for exporting data beyond the visible table rows */
  resolveExportData?: () => Promise<T[]>;
  /** Extra content rendered inside the export dropdown (e.g. status picker) */
  exportDropdownExtra?: ReactNode;

  // === SEARCH ===
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Show/hide search (default: true) */
  showSearch?: boolean;
  /** Controlled search input value */
  searchQuery?: string;
  /** Controlled search input change handler */
  onSearchQueryChange?: (query: string) => void;
  /** External search change handler (for server-side search) */
  onSearchChange?: (query: string) => void;
  /** Debounce delay in ms for external search (default: 300) */
  searchDebounceMs?: number;

  // === STYLING ===
  /** Accent color for buttons and highlights */
  accentColor?: string;
  /**
   * Keep the table header row visible while the body scrolls. Enables a
   * vertically scrollable table container and pins the `<thead>` to the top.
   */
  stickyHeader?: boolean;
  /**
   * Max height of the scrollable table container when `stickyHeader` is on.
   * Defaults to `calc(100vh - 320px)`.
   */
  stickyHeaderMaxHeight?: string;
  /** Stretch the table to fill the remaining height of its page. */
  fillHeight?: boolean;

  // === OTHER ===
  /** Row click handler */
  onRowClick?: (row: T) => void;
  /** Custom row actions renderer */
  renderRowActions?: (row: T) => ReactNode;
  /** Empty state title */
  emptyTitle?: string;
  /** Empty state description */
  emptyDescription?: string;
  /** Hide the add button in empty state (default: false) */
  hideEmptyStateButton?: boolean;
  /** Additional header actions */
  headerActions?: ReactNode;
  /** Force card view regardless of screen size */
  forceCardView?: boolean;
  /** Enable checkbox selection of loaded rows. */
  selectable?: boolean;
  /** Controlled selected row IDs. */
  selectedIds?: Array<string | number>;
  /** Selection change handler. */
  onSelectionChange?: (ids: Array<string | number>) => void;
  /** Restrict which rows can be selected. */
  isRowSelectable?: (row: T) => boolean;
  /** Accessible label for a row checkbox. */
  getSelectAriaLabel?: (row: T) => string;
  /** Accessible label for the select-all checkbox. */
  selectAllAriaLabel?: string;

  // === EXPANDABLE ROWS ===
  /** Enable expandable rows */
  expandable?: boolean;
  /** Render expanded row content */
  renderExpandedContent?: (row: T) => ReactNode;

  // === FOOTER ===
  /** Show/hide footer stats (default: true) */
  showFooterStats?: boolean;

  // === INFINITE SCROLL (server-side pagination, opt-in) ===
  /**
   * Called when the user scrolls near the bottom and more rows are available.
   * Providing this enables "server mode": the component stops filtering/sorting
   * the loaded subset client-side (those are expected to be handled server-side
   * via onSearchChange / onFilterChange) and renders an infinite-scroll sentinel.
   */
  onLoadMore?: () => void;
  /** Whether another page is available to load. */
  hasNextPage?: boolean;
  /** Whether the next page is currently being fetched. */
  isFetchingNextPage?: boolean;
  /** Total number of rows across all pages (for the footer count). */
  totalCount?: number;

  // === CONTROLLED SORTING (server-side, opt-in) ===
  /**
   * Controlled sort column key. When provided together with `onSortChange`,
   * the table delegates sorting to the parent (enabling sorting even in server
   * mode) instead of sorting the loaded subset client-side.
   */
  sortKey?: string | null;
  /** Controlled sort direction. */
  sortDirection?: "asc" | "desc";
  /** External sort change handler (for server-side sorting). */
  onSortChange?: (key: string, direction: "asc" | "desc") => void;
}

// =============================================================================
// LOADING SKELETON
// =============================================================================

const TableSkeleton: React.FC<{ columns: number; rows: number }> = ({
  columns,
  rows,
}) => (
  <>
    {Array.from({ length: rows }).map((_, rowIdx) => (
      <tr key={rowIdx} className="animate-pulse">
        {Array.from({ length: columns }).map((_, colIdx) => (
          <td key={colIdx} className="px-4 py-3">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
          </td>
        ))}
        <td className="px-4 py-3">
          <div className="h-8 w-8 bg-gray-200 rounded" />
        </td>
      </tr>
    ))}
  </>
);

const CardSkeleton: React.FC<{ count: number }> = ({ count }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, idx) => (
      <div
        key={idx}
        className="bg-white rounded-xl p-4 border border-gray-200 animate-pulse"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gray-200 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// =============================================================================
// MOBILE CARD VIEW
// =============================================================================

interface MobileCardProps<T> {
  row: T;
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  renderRowActions?: (row: T) => ReactNode;
  index: number;
  accentColor: string;
  selectable?: boolean;
  selected?: boolean;
  selectionDisabled?: boolean;
  onToggleSelect?: (row: T, selected: boolean) => void;
  selectAriaLabel?: string;
}

function MobileCard<T extends object>({
  row,
  columns,
  onRowClick,
  renderRowActions,
  index,
  accentColor,
  selectable = false,
  selected = false,
  selectionDisabled = false,
  onToggleSelect,
  selectAriaLabel,
}: MobileCardProps<T>) {
  const visibleColumns = columns
    .filter((col) => !col.hideOnMobile)
    .sort((a, b) => (a.mobilePriority || 99) - (b.mobilePriority || 99));

  const [mainColumn, ...otherColumns] = visibleColumns;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ delay: index * 0.03 }}
      onClick={() => onRowClick?.(row)}
      className={`
        bg-white rounded-xl p-4
        border border-gray-200
        shadow-sm
        ${onRowClick ? "cursor-pointer active:scale-[0.99]" : ""}
        hover:border-gray-300 hover:shadow-md
        transition-all
      `}
      style={{ "--accent-color": accentColor } as React.CSSProperties}
    >
      <div className="flex items-start justify-between gap-3">
        {selectable && (
          <div
            className="flex-shrink-0 pt-0.5"
            onClick={(event) => event.stopPropagation()}
          >
            <Checkbox
              size="small"
              checked={selected}
              disabled={selectionDisabled}
              onChange={(event) => onToggleSelect?.(row, event.target.checked)}
              inputProps={{ "aria-label": selectAriaLabel }}
              sx={{ color: accentColor, "&.Mui-checked": { color: accentColor } }}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {mainColumn?.render ? (
            mainColumn.render(row)
          ) : (
            <span className="text-sm font-medium text-gray-900">
              {String(
                (row as Record<string, unknown>)[String(mainColumn?.key)] ??
                "-",
              )}
            </span>
          )}
        </div>
        {renderRowActions && (
          <div className="flex-shrink-0">{renderRowActions(row)}</div>
        )}
      </div>

      {otherColumns.length > 0 && (
        <div className="space-y-2 pt-3 mt-3 border-t border-gray-100">
          {otherColumns.map((column) => (
            <div
              key={String(column.key)}
              className="flex items-center justify-between gap-3"
            >
              <span className="text-xs text-gray-500 font-medium flex-shrink-0">
                {column.label}
              </span>
              <div className="text-right min-w-0">
                {column.render ? (
                  column.render(row)
                ) : (
                  <span className="text-sm text-gray-900">
                    {String(
                      (row as Record<string, unknown>)[String(column.key)] ??
                      "-",
                    )}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

// =============================================================================
// EMPTY STATE
// =============================================================================

interface EmptyStateComponentProps {
  title: string;
  description: string;
  onAdd?: () => void;
  addButtonText: string;
  accentColor: string;
  addButtonDisabled?: boolean;
}

const EmptyStateComponent: React.FC<EmptyStateComponentProps> = ({
  title,
  description,
  onAdd,
  addButtonText,
  accentColor,
  addButtonDisabled = false,
}) => (
  <div className="flex flex-col items-center justify-center text-center py-8">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Search className="w-7 h-7 text-gray-400" />
    </div>
    <h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
    <p className="text-sm text-gray-500 mb-4">{description}</p>
    {onAdd && (
      <Button
        variant="contained"
        onClick={onAdd}
        disabled={addButtonDisabled}
        startIcon={<Plus size={18} />}
        sx={{
          backgroundColor: accentColor,
          borderRadius: "12px",
          textTransform: "none",
          fontWeight: 600,
          px: 3,
          py: 1,
          boxShadow: `0 4px 12px ${accentColor}40`,
          "&:hover": {
            backgroundColor: accentColor,
            boxShadow: `0 6px 16px ${accentColor}50`,
          },
          "&:disabled": {
            opacity: 0.5,
            boxShadow: "none",
          },
        }}
      >
        {addButtonText}
      </Button>
    )}
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function DataTable<T extends object>({
  // Page Header
  title,
  eyebrow,
  description,
  icon,
  iconColor,
  countBadge,
  headerCenter,
  belowHeader,

  // Data
  data,
  columns,
  getRowId,
  tableMinWidth,
  isLoading = false,
  error = null,
  body,

  // Filters
  filterConfig,
  filterValues,
  onFilterChange,
  showFilterButton = false,
  toolbarInHeader = false,
  defaultFiltersOpen = false,
  filtersOpen: filtersOpenProp,
  onFiltersOpenChange,
  filterPanelExtra,
  onClearFilters,

  // Buttons
  onAdd,
  addButtonText = "Add New",
  showAddButton,
  addButtonDisabled = false,
  onRefresh,
  showRefreshButton,
  showExportButton = false,
  exportButtonDisabled = false,
  exportFormats = ["csv", "excel", "json"],
  exportFilename = "export",
  exportColumns,
  resolveExportData,
  exportDropdownExtra,

  // Search
  searchPlaceholder = "Search...",
  showSearch = true,
  searchQuery: searchQueryProp,
  onSearchQueryChange,
  onSearchChange,
  searchDebounceMs = 300,

  // Styling
  accentColor = "#c00035",
  stickyHeader = false,
  stickyHeaderMaxHeight = "calc(100vh - 320px)",
  fillHeight = false,

  // Other
  onRowClick,
  renderRowActions,
  emptyTitle = "No data found",
  emptyDescription = "Get started by adding your first item.",
  hideEmptyStateButton = false,
  headerActions,
  forceCardView = false,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  isRowSelectable,
  getSelectAriaLabel,
  selectAllAriaLabel = "Select all",

  // Expandable Rows
  expandable = false,
  renderExpandedContent,

  // Footer
  showFooterStats = true,

  // Infinite scroll
  onLoadMore,
  hasNextPage = false,
  isFetchingNextPage = false,
  totalCount,
  sortKey: sortKeyProp,
  sortDirection: sortDirectionProp,
  onSortChange,
}: DataTableProps<T>) {
  const [internalSearchQuery, setInternalSearchQuery] = useState("");
  const isSearchControlled = searchQueryProp !== undefined;
  const searchQuery = isSearchControlled ? searchQueryProp : internalSearchQuery;
  const setSearchQuery = (value: string) => {
    if (isSearchControlled) {
      onSearchQueryChange?.(value);
      return;
    }
    setInternalSearchQuery(value);
  };
  const isSortControlled = onSortChange !== undefined;
  const [internalSortKey, setInternalSortKey] = useState<string | null>(null);
  const [internalSortDirection, setInternalSortDirection] = useState<"asc" | "desc">("asc");
  const sortKey = isSortControlled ? (sortKeyProp ?? null) : internalSortKey;
  const sortDirection = isSortControlled
    ? (sortDirectionProp ?? "asc")
    : internalSortDirection;
  const [internalShowFilters, setInternalShowFilters] = useState(defaultFiltersOpen);
  const isFiltersControlled = filtersOpenProp !== undefined;
  const showFilters = isFiltersControlled ? filtersOpenProp : internalShowFilters;
  const setShowFilters = (open: boolean) => {
    if (isFiltersControlled) {
      onFiltersOpenChange?.(open);
      return;
    }
    setInternalShowFilters(open);
  };
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set(),
  );
  const { isMobile } = useResponsive();

  // Debounced external search callback
  useEffect(() => {
    if (!onSearchChange) return;

    const timer = setTimeout(() => {
      onSearchChange(searchQuery);
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [searchQuery, onSearchChange, searchDebounceMs]);

  // Toggle row expansion
  const toggleRowExpansion = (rowId: string | number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const useCardView = forceCardView || isMobile;

  // Resolve button visibility
  const shouldShowAdd = showAddButton ?? !!onAdd;
  const shouldShowRefresh = showRefreshButton ?? !!onRefresh;
  // Skip the action bar entirely when the caller renders those controls itself
  const hasActionBar =
    showSearch ||
    (showFilterButton && !!filterConfig && filterConfig.length > 0) ||
    (shouldShowRefresh && !!onRefresh) ||
    showExportButton ||
    !!headerActions ||
    (shouldShowAdd && !!onAdd);

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    if (!filterValues) return 0;
    return Object.entries(filterValues).filter(([, val]) => {
      if (val === undefined || val === "" || val === "all") return false;
      if (Array.isArray(val)) return val.length > 0;
      return true;
    }).length;
  }, [filterValues]);

  // Server mode (infinite scroll): rows are already filtered/sorted/paginated
  // server-side, so skip client-side search and sort entirely.
  const serverMode = !!onLoadMore;

  // Filter and sort data
  const filteredData = useMemo(() => {
    let result = [...data];

    if (serverMode) {
      return result;
    }

    // Search filter (only apply client-side if no external search handler)
    if (searchQuery && !onSearchChange) {
      const query = searchQuery.toLowerCase();
      result = result.filter((row) =>
        Object.values(row).some(
          (value) =>
            value &&
            typeof value === "string" &&
            value.toLowerCase().includes(query),
        ),
      );
    }

    // Sort
    if (sortKey) {
      const sortColumn = columns.find((c) => String(c.key) === sortKey);
      const getValue = (row: T) =>
        sortColumn?.sortValue
          ? sortColumn.sortValue(row)
          : (row as Record<string, unknown>)[sortKey];

      result.sort((a, b) => {
        const aVal = getValue(a);
        const bVal = getValue(b);

        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        const comparison =
          typeof aVal === "number" && typeof bVal === "number"
            ? aVal - bVal
            : String(aVal).localeCompare(String(bVal));
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    return result;
  }, [data, searchQuery, sortKey, sortDirection, onSearchChange, serverMode, columns]);

  const selectableRows = useMemo(
    () =>
      selectable
        ? filteredData.filter((row) => isRowSelectable?.(row) ?? true)
        : [],
    [filteredData, isRowSelectable, selectable],
  );
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectableLoadedCount = selectableRows.length;
  const selectedLoadedCount = selectableRows.filter((row) =>
    selectedIdSet.has(getRowId(row)),
  ).length;
  const allLoadedSelected =
    selectableLoadedCount > 0 && selectedLoadedCount === selectableLoadedCount;
  const someLoadedSelected =
    selectedLoadedCount > 0 && selectedLoadedCount < selectableLoadedCount;

  const toggleRowSelection = (row: T, nextSelected: boolean) => {
    if (!onSelectionChange) return;
    const rowId = getRowId(row);
    const next = new Set(selectedIds);
    if (nextSelected) next.add(rowId);
    else next.delete(rowId);
    onSelectionChange(Array.from(next));
  };

  const toggleSelectAllLoaded = (nextSelected: boolean) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds);
    selectableRows.forEach((row) => {
      const rowId = getRowId(row);
      if (nextSelected) next.add(rowId);
      else next.delete(rowId);
    });
    onSelectionChange(Array.from(next));
  };

  // Infinite scroll: observe a sentinel near the bottom and request more rows.
  // The sentinel lives inside the scrollable area so it only becomes visible
  // when the user actually scrolls to the last row. When the table uses a
  // sticky header (its own fixed-height scroll container), that container is
  // the observer root; otherwise the viewport is used (page scroll).
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          onLoadMore();
        }
      },
      {
        root: !useCardView && stickyHeader ? scrollContainerRef.current : null,
        threshold: 0.1,
      },
    );

    const node = loadMoreRef.current;
    if (node) observer.observe(node);

    return () => observer.disconnect();
  }, [
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    stickyHeader,
    useCardView,
    filteredData.length,
  ]);

  const loadMoreSentinel = onLoadMore && !error && (
    <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
      {isFetchingNextPage && (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading more...</span>
        </div>
      )}
    </div>
  );

  // Client-side sorting only reorders the loaded subset, so it's disabled in
  // server mode. When a controlled `onSortChange` handler is provided, the
  // parent owns sorting (server-side), so it's allowed even in server mode.
  const canSort = (column: Column<T>) =>
    !!column.sortable && (!serverMode || isSortControlled);

  const handleSort = (key: string) => {
    const nextDirection: "asc" | "desc" =
      sortKey === key ? (sortDirection === "asc" ? "desc" : "asc") : "asc";

    if (isSortControlled) {
      onSortChange?.(key, nextDirection);
      return;
    }

    setInternalSortKey(key);
    setInternalSortDirection(nextDirection);
  };

  // Generate export columns from table columns if not provided
  const finalExportColumns: ExportColumn<T>[] = useMemo(() => {
    if (exportColumns) return exportColumns;
    return columns.map((col) => ({
      key: String(col.key),
      header: col.label,
    }));
  }, [columns, exportColumns]);

  const actionBar = hasActionBar ? (
      <div className="flex flex-wrap items-center justify-end gap-3">
        {/* Search */}
        {showSearch && (
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search className="w-4 h-4 text-gray-400" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <X className="w-4 h-4 text-gray-400" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: "280px",
              minWidth: "200px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "white",
                fontSize: "0.875rem",
                "& fieldset": { borderColor: "#e5e7eb" },
                "&:hover fieldset": { borderColor: "#d1d5db" },
                "&.Mui-focused fieldset": {
                  borderColor: accentColor,
                  borderWidth: "2px",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#9ca3af",
                opacity: 1,
              },
            }}
          />
        )}
        {/* Filter Toggle Button */}
        {showFilterButton && filterConfig && filterConfig.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<Filter size={16} />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              borderRadius: "12px",
              borderColor: activeFiltersCount > 0 ? accentColor : "#e5e7eb",
              color: activeFiltersCount > 0 ? accentColor : "#374151",
              backgroundColor:
                activeFiltersCount > 0 ? `${accentColor}10` : "transparent",
              textTransform: "none",
              fontWeight: 500,
              px: 2,
              py: 0.875,
              "&:hover": {
                borderColor: activeFiltersCount > 0 ? accentColor : "#d1d5db",
                backgroundColor:
                  activeFiltersCount > 0 ? `${accentColor}15` : "#f9fafb",
              },
            }}
          >
            Filters
            {activeFiltersCount > 0 && (
              <span
                className="ml-1.5 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center text-white"
                style={{ backgroundColor: accentColor }}
              >
                {activeFiltersCount}
              </span>
            )}
          </Button>
        )}

        {/* Refresh Button */}
        {shouldShowRefresh && onRefresh && (
          <IconButton
            onClick={onRefresh}
            disabled={isLoading}
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "10px",
              color: "#374151",
              "&:hover": {
                borderColor: "#d1d5db",
                backgroundColor: "#f9fafb",
              },
              "&:disabled": { opacity: 0.5 },
            }}
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
          </IconButton>
        )}

        {/* Export Button */}
        {showExportButton && !isMobile && (
          <ExportButton
            data={filteredData}
            resolveData={resolveExportData}
            columns={finalExportColumns}
            filename={exportFilename}
            formats={exportFormats}
            disabled={exportButtonDisabled || (!resolveExportData && filteredData.length === 0)}
            dropdownExtra={exportDropdownExtra}
          />
        )}

        {/* Additional Header Actions */}
        {headerActions}

        {/* Add Button */}
        {shouldShowAdd && onAdd && (
          <Button
            variant="contained"
            onClick={onAdd}
            disabled={addButtonDisabled}
            startIcon={<Plus size={18} />}
            sx={{
              backgroundColor: accentColor,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              py: 1,
              ml: "auto",
              boxShadow: `0 10px 15px -3px ${accentColor}40`,
              "&:hover": {
                backgroundColor: accentColor,
                boxShadow: `0 12px 20px -3px ${accentColor}50`,
              },
              "&:active": { transform: "scale(0.98)" },
              "&:disabled": {
                opacity: 0.5,
                boxShadow: "none",
              },
            }}
          >
            {addButtonText}
          </Button>
        )}
      </div>
  ) : null;

  return (
    <div className={fillHeight ? "flex min-h-0 min-w-0 flex-1 flex-col gap-4" : "space-y-4 min-w-0"}>
      {/* Page Header */}
      {title && (
        <PageHeader
          title={title}
          eyebrow={eyebrow}
          description={description}
          icon={icon}
          iconColor={iconColor}
          countBadge={countBadge}
          headerCenter={headerCenter}
          accentColor={accentColor}
          actions={toolbarInHeader ? actionBar : undefined}
          className={toolbarInHeader ? "" : "mb-6"}
        />
      )}

      {/* Action Bar */}
      {!toolbarInHeader && actionBar}

      {belowHeader}

      {/* Dynamic Filters Panel */}
      <AnimatePresence>
        {showFilters &&
          filterConfig &&
          filterConfig.length > 0 &&
          onFilterChange && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <FilterBar
                  filters={filterConfig}
                  values={filterValues || {}}
                  onChange={onFilterChange}
                  totalItems={data.length}
                  filteredItems={filteredData.length}
                  extraFilters={filterPanelExtra}
                  onClearAll={onClearFilters}
                />
              </div>
            </motion.div>
          )}
      </AnimatePresence>

      {body != null ? (
        <div className={fillHeight ? "flex min-h-0 min-w-0 flex-1 flex-col" : undefined}>
          {body}
        </div>
      ) : (
      /* Table/Cards Container */
      <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden${fillHeight ? " flex min-h-0 flex-1 flex-col" : ""}`}>
        {error ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-7 h-7 text-[#c00035]" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Error loading data
            </h3>
            <p className="text-sm text-gray-500 mb-4 text-center">{error}</p>
            {onRefresh && (
              <Button
                variant="contained"
                onClick={onRefresh}
                startIcon={<RefreshCw size={16} />}
                sx={{
                  backgroundColor: accentColor,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  "&:hover": { backgroundColor: accentColor, opacity: 0.9 },
                }}
              >
                Try Again
              </Button>
            )}
          </div>
        ) : useCardView ? (
          <div className="p-3">
            {isLoading ? (
              <CardSkeleton count={5} />
            ) : filteredData.length === 0 ? (
              <EmptyStateComponent
                title={emptyTitle}
                description={emptyDescription}
                onAdd={hideEmptyStateButton ? undefined : onAdd}
                addButtonText={addButtonText}
                accentColor={accentColor}
                addButtonDisabled={addButtonDisabled}
              />
            ) : (
              <div className="space-y-3">
                <AnimatePresence mode="popLayout">
                  {filteredData.map((row, idx) => (
                    <MobileCard
                      key={getRowId(row)}
                      row={row}
                      columns={columns}
                      onRowClick={onRowClick}
                      renderRowActions={renderRowActions}
                      index={idx}
                      accentColor={accentColor}
                      selectable={selectable}
                      selected={selectedIdSet.has(getRowId(row))}
                      selectionDisabled={!(isRowSelectable?.(row) ?? true)}
                      onToggleSelect={toggleRowSelection}
                      selectAriaLabel={getSelectAriaLabel?.(row)}
                    />
                  ))}
                </AnimatePresence>
                {loadMoreSentinel}
              </div>
            )}
          </div>
        ) : (
          <div
            ref={scrollContainerRef}
            className={stickyHeader || fillHeight ? "min-h-0 flex-1 overflow-auto" : "overflow-x-auto"}
            style={{
              containerType: "inline-size",
              ...(stickyHeader && !fillHeight ? { maxHeight: stickyHeaderMaxHeight } : {}),
            }}
          >
            <table
              className="w-full"
              style={
                tableMinWidth
                  ? { tableLayout: "fixed", minWidth: tableMinWidth }
                  : undefined
              }
            >
              <thead className={stickyHeader ? "sticky top-0 z-10" : undefined}>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  {selectable && (
                    <th className="px-2 py-3 w-10">
                      <Checkbox
                        size="small"
                        checked={allLoadedSelected}
                        indeterminate={someLoadedSelected}
                        disabled={selectableLoadedCount === 0}
                        onChange={(event) =>
                          toggleSelectAllLoaded(event.target.checked)
                        }
                        inputProps={{ "aria-label": selectAllAriaLabel }}
                        sx={{ color: accentColor, "&.Mui-checked": { color: accentColor } }}
                      />
                    </th>
                  )}
                  {/* Expand Column Header */}
                  {expandable && (
                    <th className="px-2 py-3 w-10">
                      <span className="sr-only">Expand</span>
                    </th>
                  )}
                  {columns.map((column) => (
                    <th
                      key={String(column.key)}
                      className={`
                        px-4 py-3 text-left
                        ${canSort(column) ? "cursor-pointer hover:bg-gray-100 transition-colors" : ""}
                      `}
                      style={{
                        width: column.width,
                        minWidth: column.minWidth ?? "80px",
                      }}
                      onClick={() =>
                        canSort(column) && handleSort(String(column.key))
                      }
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs font-bold text-gray-500 uppercase tracking-wider ${
                            column.width ? "whitespace-normal" : "whitespace-nowrap"
                          }`}
                        >
                          {column.label}
                        </span>
                        {canSort(column) && sortKey === String(column.key) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            {sortDirection === "asc" ? (
                              <ChevronUp
                                className="w-4 h-4"
                                style={{ color: accentColor }}
                              />
                            ) : (
                              <ChevronDown
                                className="w-4 h-4"
                                style={{ color: accentColor }}
                              />
                            )}
                          </motion.div>
                        )}
                      </div>
                    </th>
                  ))}
                  {renderRowActions && (
                    <th className="px-4 py-3 w-24 text-right">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Actions
                      </span>
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  <TableSkeleton columns={columns.length} rows={5} />
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={
                        columns.length +
                        (renderRowActions ? 1 : 0) +
                        (expandable ? 1 : 0) +
                        (selectable ? 1 : 0)
                      }
                      className="py-12"
                    >
                      <div className="sticky left-0" style={{ width: "100cqw" }}>
                        <EmptyStateComponent
                          title={emptyTitle}
                          description={emptyDescription}
                          onAdd={hideEmptyStateButton ? undefined : onAdd}
                          addButtonText={addButtonText}
                          accentColor={accentColor}
                          addButtonDisabled={addButtonDisabled}
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <AnimatePresence mode="popLayout">
                    {filteredData.map((row, idx) => {
                      const rowId = getRowId(row);
                      const isExpanded = expandedRows.has(rowId);
                      const totalColumns =
                        columns.length +
                        (renderRowActions ? 1 : 0) +
                        (expandable ? 1 : 0) +
                        (selectable ? 1 : 0);

                      // Stagger only the first rows; cap so long lists don't
                      // leave bottom rows at opacity 0 (invisible but clickable)
                      // for several seconds (idx * 0.02 grew unbounded).
                      const enterDelay = Math.min(idx, 15) * 0.02;

                      return (
                        <React.Fragment key={rowId}>
                          <motion.tr
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ delay: enterDelay }}
                            onClick={() => {
                              if (expandable && renderExpandedContent) {
                                toggleRowExpansion(rowId);
                              } else {
                                onRowClick?.(row);
                              }
                            }}
                            className={`group ${onRowClick || expandable ? "cursor-pointer" : ""} transition-colors ${isExpanded ? "bg-gray-50" : ""}`}
                            onMouseEnter={(e) => {
                              if (!isExpanded) {
                                e.currentTarget.style.background = `linear-gradient(to right, ${accentColor}08, transparent)`;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isExpanded) {
                                e.currentTarget.style.background = "";
                              }
                            }}
                          >
                            {selectable && (
                              <td
                                className="px-2 py-3 w-10"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <Checkbox
                                  size="small"
                                  checked={selectedIdSet.has(rowId)}
                                  disabled={!(isRowSelectable?.(row) ?? true)}
                                  onChange={(event) =>
                                    toggleRowSelection(row, event.target.checked)
                                  }
                                  inputProps={{
                                    "aria-label": getSelectAriaLabel?.(row),
                                  }}
                                  sx={{
                                    color: accentColor,
                                    "&.Mui-checked": { color: accentColor },
                                  }}
                                />
                              </td>
                            )}
                            {/* Expand Toggle Column */}
                            {expandable && (
                              <td className="px-2 py-3 w-10">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRowExpansion(rowId);
                                  }}
                                  className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                                  style={{ color: accentColor }}
                                >
                                  <motion.div
                                    animate={{ rotate: isExpanded ? 180 : 0 }}
                                    transition={{ duration: 0.2, ease: "easeInOut" }}
                                  >
                                    <ChevronDown className="w-4 h-4" />
                                  </motion.div>
                                </button>
                              </td>
                            )}
                            {columns.map((column) => (
                              <td
                                key={String(column.key)}
                                className="px-4 py-3"
                                style={{
                                  width: column.width,
                                  minWidth: column.minWidth,
                                }}
                              >
                                {column.render ? (
                                  column.render(row)
                                ) : (
                                  <span className="text-sm text-gray-900">
                                    {String(
                                      (row as Record<string, unknown>)[
                                      String(column.key)
                                      ] ?? "-",
                                    )}
                                  </span>
                                )}
                              </td>
                            ))}
                            {renderRowActions && (
                              <td className="px-4 py-3 text-right">
                                <div
                                  className="flex justify-end"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {renderRowActions(row)}
                                </div>
                              </td>
                            )}
                          </motion.tr>

                          {/* Expanded Content Row */}
                          <AnimatePresence initial={false}>
                            {expandable &&
                              isExpanded &&
                              renderExpandedContent && (
                                <tr className="bg-gray-50 border-b border-gray-200">
                                  <td colSpan={totalColumns} style={{ padding: 0 }}>
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.25, ease: "easeInOut" }}
                                      style={{ overflow: "hidden" }}
                                    >
                                      <div className="px-4 py-4">
                                        <div className="pl-6">
                                          {renderExpandedContent(row)}
                                        </div>
                                      </div>
                                    </motion.div>
                                  </td>
                                </tr>
                              )}
                          </AnimatePresence>
                        </React.Fragment>
                      );
                    })}
                  </AnimatePresence>
                )}
              </tbody>
            </table>
            {loadMoreSentinel}
          </div>
        )}
      </div>
      )}

      {/* Footer Stats */}
      {showFooterStats && body == null && !isLoading && !error && (
        <div className="text-xs text-gray-500 px-1">
          Showing{" "}
          <span className="font-semibold text-gray-900">
            {filteredData.length}
          </span>{" "}
          of{" "}
          <span className="font-semibold text-gray-900">
            {totalCount ?? data.length}
          </span>{" "}
          results
        </div>
      )}
    </div>
  );
}

export default DataTable;
